import { api, RouteAPI } from "../service/apiService";

export enum DirectoryType {
    PERSONAL = "personal",
    SHARED = "shared",
    MISSION = "mission",
}

export interface Directory {
    id: string;
    name: string;
    type: DirectoryType;
    owner?: {
        id: string;
        name: string;
    };
}

export interface DirectoryWithDocuments extends Directory {
    documents: Document[];
}

export enum DocumentType {
    RESUME = "resume",
    DIPLOMA = "diploma",
    TRAINING_CERTIFICATE = "training_certificate",
    CERTIFICATIONS = "certifications",
    INSURANCE = "insurance",
    INVOICE = "invoice",
    QUOTE = "quote",
    TIMETABLE = "timetable",
    OFFICIAL_APPROVAL = "official_approval",
}

export interface Document {
    id: string;
    name: string;
    type: DocumentType;
    fileUri: string;
    created_at: string;
    updated_at: string;
}

export type CompanyDocuments = {
    company: string;
    documents: Document[];
};

export type DocumentCategory = "personal" | "shared" | "mission";

export interface DirectoryQuery {
    type?: DocumentCategory;
    name?: string;
    "owner.id"?: string;
}

export const fetchDirectories = async (
    query: DirectoryQuery = {}
): Promise<Directory[]> => {
    const response = await api(RouteAPI.DIRECTORIES, "GET", {}, query);

    if (response.status !== 200 || !Array.isArray(response.data)) {
        throw new Error("Invalid response");
    }

    const formattedDirectories = response.data.map((directory: any) => ({
        ...directory,
        owner: directory.owner
            ? {
                  id: directory.owner.id,
                  name: `${directory.owner.prenom ?? ""} ${
                      directory.owner.nom ?? ""
                  } ${directory.owner.societe ?? ""}`.trim(),
              }
            : null,
    }));

    return formattedDirectories as Directory[];
};

export const fetchDirectory = async (
    id: string
): Promise<DirectoryWithDocuments> => {
    const response = await api(`${RouteAPI.DIRECTORIES}/${id}`, "GET");

    if (response.status !== 200) {
        throw new Error("Invalid response");
    }

    const formattedDirectory = {
        ...response.data,
        owner: response.data.owner
            ? {
                  id: response.data.owner.id,
                  name: `${response.data.owner.prenom ?? ""} ${
                      response.data.owner.nom ?? ""
                  } ${response.data.owner.societe ?? ""}`.trim(),
              }
            : null,
    };

    return formattedDirectory as DirectoryWithDocuments;
};

export const uploadDocument = async (
    file: File,
    metadada: {
        type: DocumentType;
        name: string;
        directoryId: string;
    }
): Promise<void> => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("type", metadada.type);
    formData.append("name", metadada.name);
    formData.append("directoryId", metadada.directoryId);

    const response = await api(RouteAPI.DOCUMENTS, "POST", formData);

    if (response.status !== 201) {
        throw new Error("Failed to upload document");
    }

    return;
};

export const deleteDocument = async (id: string): Promise<void> => {
    const response = await api(`${RouteAPI.DOCUMENTS}/${id}`, "DELETE");

    if (response.status !== 204) {
        throw new Error("Failed to delete document");
    }

    return;
};

export const renameDocument = async (
    id: string,
    newName: string
): Promise<void> => {
    const response = await api(`${RouteAPI.DOCUMENTS}/${id}`, "PATCH", {
        name: newName,
    });

    if (response.status !== 200) {
        throw new Error("Failed to rename document");
    }

    return;
};
